import React, { useState, useCallback, useEffect } from "react";
import { Table, Collapse } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { PrimaryButton } from "../../../StyledCss/styledbtn";
import "./index.css";
import { exportEmployeeRevenue } from "../../../api/services/hroneApi";
import { formatINR } from "../../../util/indianCurrency";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";

const getInitials = (name) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const BAR_COLORS = {
  revenue: "#764ba2",
  received_revenue: "#48bb78",
  pending_revenue: "#ed8936",
  difference_revenue: "#fc8181",
};

const renderTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="inline-chart-tooltip">
        <p className="label">{label}</p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ color: p.fill, margin: 0 }}>
            {p.name}: {formatINR(p.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ALL_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const buildChartData = (monthlyRevenue = []) => {
  const map = Object.fromEntries(
    monthlyRevenue.map((d) => [d.month_id - 1, d]),
  );
  return ALL_MONTHS.map((month, i) => ({
    month,
    revenue: map[i]?.revenue ?? 0,
    received_revenue: map[i]?.received_revenue ?? 0,
    pending_revenue: map[i]?.pending_revenue ?? 0,
    difference_revenue: map[i]?.difference_revenue ?? 0,
  }));
};

const ITEMS_PER_PAGE = 7;

const UserRevenueTable = ({
  users,
  filterProjectId,
  startMonth,
  endMonth,
  selectedProject,
}) => {
  const { hasPermission } = usePermissions();
  const canExportEmployeeRevenue = hasPermission(FEATURE_CODES.REVENUE_EMPLOYEE_EXPORT);
  const [expandedRow, setExpandedRow] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const toggleRow = useCallback((id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterProjectId]);

  const filteredUsers = users.filter((u) =>
    u.employee_name?.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const handleExportEmployeeRevenue = async () => {
    try {
      setExporting(true);
      const response = await exportEmployeeRevenue(
        selectedProject,
        startMonth.toISOString().slice(0, 7),
        endMonth.toISOString().slice(0, 7),
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const contentDisposition = response.headers.get("content-disposition");
        let filename;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match) {
            filename = match[1];
          }
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Employee Revenue exported successfully");
      } else {
        toast.error("Failed to export Employee revenue");
      }
    } catch (error) {
      console.error("Error downloading Employee revenue:", error);
      toast.error("Failed to download Employee revenue");
    }
    finally{
      setExporting(false);
    }
  };

  return (
    <div className="user-revenue-table">
      <Toaster position="top-right" />
      <div className="table-header">
        <h5 className="table-title">Employee-Wise Revenue</h5>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="user-search"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          {canExportEmployeeRevenue && (
            <PrimaryButton onClick={handleExportEmployeeRevenue} disabled={exporting}>
              {exporting ? "Exporting..." : "Export"}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className="table-wrapper">
        <Table responsive hover>
          <thead>
            <tr>
              <th style={{ width: "40px" }}></th>
              <th>EMP ID</th>
              <th>User Name</th>
              <th>Total Revenue</th>
            <th>Recieved Revenue</th>
            <th>Difference Revenue</th>
            <th>Pending Revenue</th>
              <th>Associated Projects</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user, index) => (
              <React.Fragment key={user.employee_id}>
                <tr
                  onClick={() => toggleRow(user.employee_id)}
                  className={`clickable-row ${
                    expandedRow === user.employee_id ? "expanded" : ""
                  }`}
                >
                  <td className="expand-icon">
                    {expandedRow === user.employee_id ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </td>
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>
                    <div className="user-name">
                      <span className="user-avatar">
                        {getInitials(user.employee_name)}
                      </span>
                      {user.employee_name}
                    </div>
                  </td>
                  <td className="revenue-amount">
                    {formatINR(user.employee_revenue)}
                  </td>
                  <td className="revenue-amount">
                    {formatINR(user.received_revenue)}
                  </td>
                  <td className="revenue-amount">
                    {formatINR(user.difference_revenue)}
                  </td>
                  <td className="revenue-amount">
                    {formatINR(user.pending_revenue)}
                  </td>
                  <td>
                    <div className="project-badges">
                      {user.associated_projects.map((project) => (
                        <span key={project} className="project-badge">
                          {project}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
                <tr className="chart-row">
                  <td colSpan={8} className="p-0">
                    <Collapse in={expandedRow === user.employee_id}>
                      <div className="inline-chart-container">
                        <h6 className="inline-chart-title">
                          Monthly Revenue - {user.employee_name}
                        </h6>
                        <div className="inline-chart">
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart
                              data={buildChartData(user.monthly_breakdown)}
                              barCategoryGap="10%"
                              barGap={2}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f0f0f0"
                              />
                              <XAxis dataKey="month" />
                              <YAxis
                                tickFormatter={(v) =>
                                  
                                    `₹${v / 1000}k`
                                 
                                }
                              />
                              <Tooltip
                                content={(props) =>
                                  renderTooltip(props)
                                }
                              />
                              <Bar
                                dataKey="revenue"
                                name="Revenue"
                                fill={BAR_COLORS.revenue}
                                radius={[2, 2, 0, 0]}
                                barSize={30}
                              />
                              <Bar
                                dataKey="received_revenue"
                                name="Received"
                                fill={BAR_COLORS.received_revenue}
                                radius={[2, 2, 0, 0]}
                                barSize={30}
                              />
                              <Bar
                                dataKey="pending_revenue"
                                name="Pending"
                                fill={BAR_COLORS.pending_revenue}
                                radius={[2, 2, 0, 0]}
                                barSize={30}
                              />
                              <Bar
                                dataKey="difference_revenue"
                                name="Difference"
                                fill={BAR_COLORS.difference_revenue}
                                radius={[2, 2, 0, 0]}
                                barSize={30}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </React.Fragment>
            ))}

            {paginatedUsers.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  No employees found for the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="table-pagination">
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <div className="pagination-controls">
            <button
              className="page-btn"
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? "active" : ""}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="page-btn"
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRevenueTable;
